import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabaseClient } from '../../config/supabase';
import {
    conversationPath, isShortLinkCode, SHORT_LINK_NOT_AVAILABLE, shortLinkCodeFromPath, shortLinkErrorMessage,
} from '../../lib/conversationShortLink';

/**
 * BL-177 Step 2d: /c/:code, the short link in a staff text alert.
 *
 * Only reached signed in: CRMApp shows the sign-in screen at this same address while there is no session, so after a
 * normal sign-in this page mounts and does its job. The database decides who may open the link
 * (resolve_conversation_short_link: the link's company + the person's normal access to that conversation); a "no" is the
 * same answer whatever the reason, so the page can't tell a stranger anything either.
 */
export default function ConversationShortLinkPage({ client = supabaseClient }) {
    // Read from the address (not useParams) so the page is inert anywhere but /c/<code>.
    const code = shortLinkCodeFromPath(useLocation().pathname);
    const navigate = useNavigate();
    const [error, setError] = useState('');

    useEffect(() => {
        let active = true;
        if (code === null) return undefined;
        if (!isShortLinkCode(code)) {
            setError(SHORT_LINK_NOT_AVAILABLE);
            return undefined;
        }
        setError('');
        client.rpc('resolve_conversation_short_link', { p_code: code }).then(({ data, error: rpcError }) => {
            if (!active) return;
            const path = rpcError ? '' : conversationPath(data);
            if (path) navigate(path, { replace: true });
            else setError(rpcError ? shortLinkErrorMessage(rpcError) : SHORT_LINK_NOT_AVAILABLE);
        }, () => {
            if (active) setError(shortLinkErrorMessage(null));
        });
        return () => { active = false; };
    }, [client, code, navigate]);

    if (code === null) return null;
    if (!error) return <div role="status" className="p-8 text-center text-gray-400">Opening the conversation…</div>;
    return (
        <div role="alert" className="mx-auto max-w-md p-8 text-center">
            <p className="mb-4 text-gray-600">{error}</p>
            <button type="button" className="rounded-lg bg-slate-900 px-4 py-2 font-semibold text-white"
                onClick={() => navigate('/communications', { replace: true })}>
                Go to Communications
            </button>
        </div>
    );
}
